/**
 * RFC-109: App-Based Template Onboarding Setup
 *
 * Full page wizard for selecting frameworks, apps, and templates during onboarding.
 * Accessible via sidebar menu item "Setup Guide".
 *
 * Flow: Framework Selection -> App Selection -> Template Review -> Complete
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Check,
  ChevronRight,
  Info,
  Loader2,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod/v3';

import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getAppTypeInfo } from '@/utils/app-type';
import { AppIcon, CATEGORY_ICONS } from '@/utils/app-icons';
import { useWorkspaceType } from '@/hooks/useWorkspaceType';
import { TestDetailsTab } from '@/pages/(protected)/workspaces/[workspaceId]/compliance/tests/[testId]/components/TestDetailsTab';

const setupSearchSchema = z.object({
  framework: z.string().optional(),
});

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/setup/'
)({
  validateSearch: setupSearchSchema,
  component: SetupPage,
});

type Step = 'frameworks' | 'apps' | 'templates';

export default function SetupPage() {
  const navigate = useNavigate();
  const { workspaceId } = Route.useParams();
  const { framework: frameworkSlugFromUrl } = Route.useSearch();
  const { enabledFrameworks: workspaceEnabledFrameworks } = useWorkspaceType();
  const [step, setStep] = useState<Step>('frameworks');
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);

  // Fetch available frameworks
  const frameworksQuery = useQuery(
    trpc.onboarding.getAvailableFrameworks.queryOptions()
  );

  // Get list of already enabled framework slugs (these will be shown as disabled)
  const alreadyEnabledSlugs = useMemo(() => {
    return new Set(workspaceEnabledFrameworks || []);
  }, [workspaceEnabledFrameworks]);

  // If framework slug is in URL, pre-select it (but stay on frameworks step)
  useEffect(() => {
    if (
      frameworkSlugFromUrl &&
      frameworksQuery.data &&
      !initializedFromUrl
    ) {
      // Find framework by slug
      const framework = frameworksQuery.data.find(
        (f) => f.slug === frameworkSlugFromUrl
      );
      if (framework && framework.isAvailable) {
        setSelectedFrameworks([framework.id]);
        setInitializedFromUrl(true);
      }
    }
  }, [frameworkSlugFromUrl, frameworksQuery.data, initializedFromUrl]);

  // Get the selected framework ID (for filtering apps/templates)
  const selectedFrameworkId = useMemo(() => {
    if (selectedFrameworks.length === 0 || !frameworksQuery.data)
      return undefined;
    return selectedFrameworks[0]; // Use first selected framework
  }, [selectedFrameworks, frameworksQuery.data]);

  // Fetch app catalog (filtered by selected framework)
  const appCatalogQuery = useQuery({
    ...trpc.onboarding.getAppCatalog.queryOptions({
      betaOnly: true,
      frameworkId: selectedFrameworkId,
    }),
    enabled: step === 'apps' && !!selectedFrameworkId,
  });

  // Fetch all templates for the selected framework (no app filtering)
  const templatesQuery = useQuery({
    ...trpc.onboarding.getRecommendedTemplates.queryOptions({
      appTypes: [], // No app filtering - get all templates for framework
      frameworkId: selectedFrameworkId,
    }),
    enabled: step === 'templates' && !!selectedFrameworkId,
  });

  // Complete setup mutation
  const completeSetupMutation = useMutation(
    trpc.onboarding.completeSetup.mutationOptions({
      onSuccess: (data) => {
        toast.success(`Created ${data.clonedCount} evidence tasks`);
        // Redirect directly to the framework page
        if (selectedFrameworks.length > 0) {
          navigate({
            to: '/workspaces/$workspaceId/compliance/$frameworkId',
            params: { workspaceId, frameworkId: selectedFrameworks[0] },
          });
        } else {
          navigate({
            to: '/workspaces/$workspaceId/compliance',
            params: { workspaceId },
          });
        }
      },
      onError: (error) => {
        toast.error(`Failed to complete setup: ${error.message}`);
      },
    })
  );

  // Get all template IDs for select all functionality
  const allTemplateIds = useMemo(() => {
    if (!templatesQuery.data?.templates) return [];
    return templatesQuery.data.templates.map((t) => t.id);
  }, [templatesQuery.data?.templates]);

  // Track if we've auto-selected on initial load
  const hasAutoSelected = useRef(false);

  // Auto-select all templates when first entering templates step
  useEffect(() => {
    if (
      step === 'templates' &&
      allTemplateIds.length > 0 &&
      !hasAutoSelected.current
    ) {
      setSelectedTemplates(allTemplateIds);
      hasAutoSelected.current = true;
    }
    // Reset when leaving templates step
    if (step !== 'templates') {
      hasAutoSelected.current = false;
    }
  }, [step, allTemplateIds]);

  // Select framework (single-select - only one framework at a time)
  const selectFramework = (frameworkId: string, isAvailable: boolean) => {
    if (!isAvailable) return;
    // Single-select: clicking selected item deselects it, clicking another replaces selection
    setSelectedFrameworks((prev) =>
      prev.includes(frameworkId) ? [] : [frameworkId]
    );
  };

  // Handle custom framework creation - navigate to controls management page
  const handleFrameworkCreated = (frameworkId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/$frameworkId/controls',
      params: { workspaceId, frameworkId },
    });
  };

  // Toggle app selection
  const toggleApp = (appType: string) => {
    setSelectedApps((prev) =>
      prev.includes(appType)
        ? prev.filter((a) => a !== appType)
        : [...prev, appType]
    );
  };

  // Toggle template selection
  const toggleTemplate = (templateId: string) => {
    setSelectedTemplates((prev) =>
      prev.includes(templateId)
        ? prev.filter((t) => t !== templateId)
        : [...prev, templateId]
    );
  };

  // Toggle app expansion
  const toggleExpanded = (appType: string) => {
    setExpandedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appType)) {
        next.delete(appType);
      } else {
        next.add(appType);
      }
      return next;
    });
  };

  // Navigation handlers
  // Skip apps step - go directly to templates
  const handleNextToTemplates = () => {
    setStep('templates');
  };

  // Keep for when we re-enable apps step
  const _handleNextToApps = () => {
    setStep('apps');
  };

  // Complete setup
  const handleComplete = () => {
    completeSetupMutation.mutate({
      selectedFrameworkIds: selectedFrameworks,
      selectedApps: selectedApps as never[],
      selectedTemplateIds: selectedTemplates,
    });
  };

  // Go to workspace dashboard
  const handleGoToDashboard = () => {
    navigate({
      to: '/workspaces/$workspaceId',
      params: { workspaceId },
    });
  };

  return (
    <Main>
      <MainHeader />

      <div className="mx-auto box-content flex max-w-[1008px] flex-1 flex-col px-6 pb-12">
        {step === 'frameworks' && (
          <FrameworkSelectionStep
            frameworks={frameworksQuery.data || []}
            isLoading={frameworksQuery.isLoading}
            selectedFrameworks={selectedFrameworks}
            alreadyEnabledSlugs={alreadyEnabledSlugs}
            onSelectFramework={selectFramework}
            onFrameworkCreated={handleFrameworkCreated}
            onNext={handleNextToTemplates}
            onSkip={handleGoToDashboard}
          />
        )}

        {/* Apps step hidden for now - will re-enable when we have more app-specific templates */}
        {/* {step === 'apps' && (
          <AppSelectionStep
            categories={appCatalogQuery.data || []}
            isLoading={appCatalogQuery.isLoading}
            selectedApps={selectedApps}
            onToggleApp={toggleApp}
            onNext={handleNextToTemplates}
            onBack={() => setStep('frameworks')}
            onSkip={handleGoToDashboard}
          />
        )} */}

        {step === 'templates' && (
          <TemplateReviewStep
            templates={templatesQuery.data?.templates || []}
            totalCount={templatesQuery.data?.total || 0}
            isLoading={templatesQuery.isLoading}
            selectedTemplates={selectedTemplates}
            frameworkName={
              frameworksQuery.data?.find((f) => f.id === selectedFrameworks[0])
                ?.name
            }
            onToggleTemplate={toggleTemplate}
            onSelectAll={() => setSelectedTemplates(allTemplateIds)}
            onDeselectAll={() => setSelectedTemplates([])}
            onBack={() => setStep('frameworks')}
            onComplete={handleComplete}
            isCompleting={completeSetupMutation.isPending}
          />
        )}
      </div>
    </Main>
  );
}

// Step 1: Framework Selection (single-select - one framework at a time)
interface FrameworkSelectionStepProps {
  frameworks: Array<{
    id: string;
    name: string;
    slug: string;
    isAvailable: boolean;
  }>;
  isLoading: boolean;
  selectedFrameworks: string[];
  alreadyEnabledSlugs: Set<string>;
  onSelectFramework: (frameworkId: string, isAvailable: boolean) => void;
  onFrameworkCreated: (frameworkId: string) => void;
  onNext: () => void;
  onSkip: () => void;
}

function FrameworkSelectionStep({
  frameworks,
  isLoading,
  selectedFrameworks,
  alreadyEnabledSlugs,
  onSelectFramework,
  onFrameworkCreated,
  onNext,
  onSkip,
}: FrameworkSelectionStepProps) {
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');

  const createFrameworkMutation = useMutation(
    trpc.guidedTemplate.createFramework.mutationOptions({
      onSuccess: (data) => {
        toast.success('Custom framework created');
        setShowCustomDialog(false);
        setCustomName('');
        setCustomDescription('');
        // Select the newly created framework
        onFrameworkCreated(data.id);
      },
      onError: (error) => {
        toast.error(`Failed to create framework: ${error.message}`);
      },
    })
  );

  const handleCreateCustom = () => {
    if (!customName.trim()) {
      toast.error('Please enter a framework name');
      return;
    }
    const slug = customName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    createFrameworkMutation.mutate({
      name: customName.trim(),
      slug,
      description: customDescription.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
            Select Framework
          </div>
          <div className="text-muted-foreground text-sm leading-5">
            Choose the compliance framework you need to comply with.
          </div>
        </div>
      </div>

      {/* Framework list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {frameworks.map((framework) => {
            const isSelected = selectedFrameworks.includes(framework.id);
            const isAlreadyEnabled = alreadyEnabledSlugs.has(framework.slug);
            const isAvailable = framework.isAvailable && !isAlreadyEnabled;

            return (
              <button
                key={framework.id}
                type="button"
                onClick={() => onSelectFramework(framework.id, isAvailable)}
                disabled={!isAvailable}
                className={cn(
                  'group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all',
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]'
                    : isAvailable
                      ? 'border-border hover:border-foreground/20 hover:bg-muted/50'
                      : 'cursor-not-allowed border-border/50 opacity-60'
                )}
              >
                {/* Checkbox circle */}
                <div
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                    isSelected || isAlreadyEnabled
                      ? 'border-primary bg-primary'
                      : isAvailable
                        ? 'border-muted-foreground/30 group-hover:border-muted-foreground/50'
                        : 'border-muted-foreground/20'
                  )}
                >
                  {(isSelected || isAlreadyEnabled) && <Check className="size-3 text-primary-foreground" />}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{framework.name}</span>
                    {isAlreadyEnabled && (
                      <Badge
                        variant="secondary"
                        className="text-xs"
                      >
                        Already added
                      </Badge>
                    )}
                    {!isAlreadyEnabled && isAvailable && !isSelected && (
                      <Badge
                        variant="outline"
                        className="border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400"
                      >
                        Available
                      </Badge>
                    )}
                    {!framework.isAvailable && (
                      <span className="text-xs text-muted-foreground">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Custom Framework option */}
          <button
            type="button"
            onClick={() => setShowCustomDialog(true)}
            className="group flex w-full items-center gap-4 rounded-xl border border-dashed border-border p-4 text-left transition-all hover:border-foreground/20 hover:bg-muted/50"
          >
            <div className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground/30 group-hover:border-muted-foreground/50">
              <Plus className="size-3 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <span className="font-medium">Custom Framework</span>
              <p className="text-sm text-muted-foreground">
                Create your own compliance framework
              </p>
            </div>
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          onClick={onSkip}
          className="text-muted-foreground hover:text-foreground"
        >
          Skip for now
        </Button>
        <Button
          onClick={onNext}
          disabled={selectedFrameworks.length === 0}
          className="gap-1"
        >
          Continue
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Custom Framework Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Framework</DialogTitle>
            <DialogDescription>
              Define your own compliance framework with custom controls and requirements.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom-name">Framework Name</Label>
              <Input
                id="custom-name"
                placeholder="e.g., Internal Security Policy"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-description">Description (optional)</Label>
              <Textarea
                id="custom-description"
                placeholder="Describe the purpose of this framework..."
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustom}
              disabled={!customName.trim() || createFrameworkMutation.isPending}
            >
              {createFrameworkMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Create Framework'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Step 2: App Selection
interface AppSelectionStepProps {
  categories: Array<{
    category: string;
    label: string;
    apps: Array<{
      appType: string;
      label: string;
      templateCount: number;
      hasTemplates: boolean;
    }>;
  }>;
  isLoading: boolean;
  selectedApps: string[];
  onToggleApp: (appType: string) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

function AppSelectionStep({
  categories,
  isLoading,
  selectedApps,
  onToggleApp,
  onNext,
  onBack,
  onSkip,
}: AppSelectionStepProps) {
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestAppName, setRequestAppName] = useState('');
  const [requestAppUrl, setRequestAppUrl] = useState('');
  const [requestNotes, setRequestNotes] = useState('');

  const requestSupportMutation = useMutation(
    trpc.onboarding.requestAppSupport.mutationOptions({
      onSuccess: () => {
        toast.success('Thank you! We\'ll review your request.');
        setShowRequestDialog(false);
        setRequestAppName('');
        setRequestAppUrl('');
        setRequestNotes('');
      },
      onError: (error) => {
        toast.error(`Failed to submit request: ${error.message}`);
      },
    })
  );

  const handleSubmitRequest = () => {
    if (!requestAppName.trim()) {
      toast.error('Please enter an app name');
      return;
    }
    requestSupportMutation.mutate({
      appName: requestAppName.trim(),
      appUrl: requestAppUrl.trim() || undefined,
      notes: requestNotes.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
            Select Apps
          </div>
          <div className="text-muted-foreground text-sm leading-5">
            Choose the tools in your stack to get recommended evidence templates.
          </div>
        </div>
      </div>

      {/* App categories */}
      {isLoading ? (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-[72px] w-full rounded-xl" />
                <Skeleton className="h-[72px] w-full rounded-xl" />
                <Skeleton className="h-[72px] w-full rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map((category) => {
            const CategoryIcon = CATEGORY_ICONS[category.category];
            return (
              <div key={category.category}>
                <div className="mb-3 flex items-center gap-2">
                  {CategoryIcon && (
                    <CategoryIcon className="size-4 text-muted-foreground" />
                  )}
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {category.label}
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {category.apps.map((app) => {
                    const isSelected = selectedApps.includes(app.appType);

                    return (
                      <button
                        key={app.appType}
                        type="button"
                        onClick={() =>
                          app.hasTemplates && onToggleApp(app.appType)
                        }
                        disabled={!app.hasTemplates}
                        className={cn(
                          'group flex items-center gap-3 rounded-xl border p-3 text-left transition-all',
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]'
                            : app.hasTemplates
                              ? 'border-border hover:border-foreground/20 hover:bg-muted/50'
                              : 'cursor-not-allowed border-border/50 opacity-50'
                        )}
                      >
                        {/* App Logo */}
                        <AppIcon appType={app.appType} className="shrink-0" />

                        {/* Content */}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {app.label}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {app.hasTemplates
                              ? `${app.templateCount} template${app.templateCount !== 1 ? 's' : ''}`
                              : 'Coming soon'}
                          </div>
                        </div>

                        {/* Selected indicator */}
                        {isSelected && (
                          <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary">
                            <Check className="size-3 text-primary-foreground" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Request support */}
          <div className="rounded-xl border border-dashed p-4 text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Don't see your app?
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowRequestDialog(true)}
            >
              <Plus className="size-4" />
              Request Support
            </Button>
          </div>
        </div>
      )}

      {/* Request Support Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request App Support</DialogTitle>
            <DialogDescription>
              Tell us which app you'd like us to add templates for.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="appName">App Name *</Label>
              <Input
                id="appName"
                placeholder="e.g., CircleCI, Buildkite, PagerDuty"
                value={requestAppName}
                onChange={(e) => setRequestAppName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="appUrl">App URL (optional)</Label>
              <Input
                id="appUrl"
                placeholder="https://example.com"
                value={requestAppUrl}
                onChange={(e) => setRequestAppUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any specific compliance requirements or use cases..."
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRequestDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              disabled={!requestAppName.trim() || requestSupportMutation.isPending}
            >
              {requestSupportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="ghost"
            onClick={onSkip}
            className="text-muted-foreground hover:text-foreground"
          >
            Skip
          </Button>
        </div>
        <Button
          onClick={onNext}
          className="gap-1"
        >
          Continue
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// Step 2: Template Review (Step 3 when apps step is enabled)
interface TemplateReviewStepProps {
  templates: Array<{
    id: string;
    title: string;
    controlId: string | null;
    controlTitle: string | null;
    templateType: string;
    taskNumber: number;
  }>;
  totalCount: number;
  isLoading: boolean;
  selectedTemplates: string[];
  frameworkName?: string;
  onToggleTemplate: (templateId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBack: () => void;
  onComplete: () => void;
  isCompleting: boolean;
}

function TemplateReviewStep({
  templates,
  totalCount,
  isLoading,
  selectedTemplates,
  frameworkName,
  onToggleTemplate,
  onSelectAll,
  onDeselectAll,
  onBack,
  onComplete,
  isCompleting,
}: TemplateReviewStepProps) {
  const allSelected = templates.length > 0 && selectedTemplates.length === templates.length;
  const someSelected = selectedTemplates.length > 0 && selectedTemplates.length < templates.length;

  // State for preview drawer
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const previewTemplate = templates.find((t) => t.id === previewTemplateId);

  // Fetch template details when preview is open
  const templateDetailsQuery = useQuery({
    ...trpc.guidedTemplate.getTemplate.queryOptions({ id: previewTemplateId! }),
    enabled: !!previewTemplateId,
  });

  return (
    <div className="flex flex-1 flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
            Select Tests{frameworkName ? ` for ${frameworkName}` : ''}
          </div>
          <div className="text-muted-foreground text-sm leading-5">
            {totalCount} recommended tests. Deselect any you don't need.
          </div>
        </div>
      </div>

      {/* Select all / Deselect all */}
      {!isLoading && templates.length > 0 && (
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() => allSelected ? onDeselectAll() : onSelectAll()}
              className={someSelected ? 'data-[state=checked]:bg-primary/50' : ''}
            />
            <span className="text-sm font-medium">
              {allSelected ? 'Deselect all' : 'Select all'}
            </span>
          </label>
          <span className="text-sm text-muted-foreground">
            {selectedTemplates.length} of {templates.length} selected
          </span>
        </div>
      )}

      {/* Templates list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center">
          <p className="text-muted-foreground">
            No recommended tests available for this framework yet.
          </p>
        </div>
      ) : (
        <div className="space-y-1 min-h-0 flex-1 overflow-y-auto pr-2">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/30"
            >
              <Checkbox
                checked={selectedTemplates.includes(template.id)}
                onCheckedChange={() => onToggleTemplate(template.id)}
                id={`template-${template.id}`}
              />
              <label
                htmlFor={`template-${template.id}`}
                className="min-w-0 flex-1 cursor-pointer"
              >
                <div className="text-sm font-medium">
                  {template.title}
                </div>
                {template.controlId && (
                  <div className="text-xs text-muted-foreground">
                    {template.controlId}
                    {template.controlTitle && ` · ${template.controlTitle}`}
                  </div>
                )}
              </label>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  setPreviewTemplateId(template.id);
                }}
              >
                <Info className="h-4 w-4" />
                <span className="sr-only">View details</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={onComplete}
          disabled={isCompleting}
        >
          {isCompleting ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Creating tests...
            </>
          ) : selectedTemplates.length === 0 ? (
            'Continue'
          ) : (
            <>
              Create {selectedTemplates.length} test
              {selectedTemplates.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>

      {/* Test Details Preview Drawer */}
      <Drawer direction="right" open={!!previewTemplateId} onOpenChange={(open) => !open && setPreviewTemplateId(null)}>
        <DrawerContent className="h-full w-[400px] sm:w-[540px] overflow-y-auto">
          <div className="p-6">
            <DrawerHeader className="p-0 pb-4">
              <DrawerTitle className="text-lg font-semibold">
                {previewTemplate?.title || 'Test Details'}
              </DrawerTitle>
              {previewTemplate?.controlId && (
                <p className="text-sm text-muted-foreground">
                  {previewTemplate.controlId}
                  {previewTemplate.controlTitle && ` · ${previewTemplate.controlTitle}`}
                </p>
              )}
            </DrawerHeader>

            {templateDetailsQuery.isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : templateDetailsQuery.data ? (
              <TestDetailsTab
                scoringCriteria={templateDetailsQuery.data.scoringCriteria}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Info className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No details available for this test</p>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
